/**
 * Generated mutation hooks. Each entity exposes typed `useCreate.X`,
 * `useUpdate.X`, `useDelete.X` hooks that resolve invalidation prefixes from
 * the registry and call the standard `request` utility.
 *
 * Callers supply per-mutation endpoint + body shape; the registry supplies
 * invalidation. Use `extraInvalidates` to add cross-entity prefixes that
 * aren't worth promoting to the registry.
 *
 * No imperative jotai store reads happen in this module — all context is
 * obtained via React hooks.
 */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiError, request } from '@/api/apiUtils';
import { type EntityActions, type EntityName, getPrefixes } from './registry';

type Prefix = readonly unknown[];
type PrefixList = readonly Prefix[];
type HttpMethod = 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface MutationOverride<TParams, TData> {
    /** HTTP verb. Defaults to 'POST' (which is correct for the Laravel `?_method=PUT` convention). */
    method?: HttpMethod;
    /** Build the URL from params. Required — the registry intentionally does not guess URL shapes. */
    endpoint: (params: TParams) => string;
    /** Build the request body. Omit for DELETE-style requests. FormData is supported and passed through. */
    body?: (params: TParams) => unknown;
    /** Extra prefixes to invalidate beyond the registry. Static or dynamic. */
    extraInvalidates?: PrefixList | ((params: TParams) => PrefixList);
    /** Run after the mutation succeeds, before invalidation. */
    onSuccess?: (data: TData, params: TParams) => void;
}

interface BuildOpts<TParams, TData> extends MutationOverride<TParams, TData> {
    entity: EntityName;
    action: string;
    /** Hook display name for DevTools / stack traces. */
    name: string;
}

function buildHook<TParams, TData>(opts: BuildOpts<TParams, TData>): () => UseMutationResult<TData, ApiError, TParams> {
    const { entity, action, method = 'POST', endpoint, body, extraInvalidates, onSuccess, name } = opts;

    const hook = function useEntityMutation() {
        const queryClient = useQueryClient();

        return useMutation<TData, ApiError, TParams>({
            mutationFn: async (params: TParams) => {
                const url = endpoint(params);
                const init: RequestInit = { method };
                if (body) {
                    const b = body(params);
                    init.body = b instanceof FormData ? b : JSON.stringify(b);
                }
                return request<TData>(url, init);
            },
            onSettled: (_data, _error, params) => {
                const prefixes = getPrefixes(entity as EntityName, action as EntityActions[EntityName]);
                for (const key of prefixes) {
                    queryClient.invalidateQueries({ queryKey: key as unknown[] });
                }
                if (extraInvalidates && params !== undefined) {
                    const extras = typeof extraInvalidates === 'function' ? extraInvalidates(params) : extraInvalidates;
                    for (const key of extras) {
                        queryClient.invalidateQueries({ queryKey: key as unknown[] });
                    }
                }
            },
            onSuccess: (data, params) => {
                onSuccess?.(data, params);
            },
        });
    };

    Object.defineProperty(hook, 'name', { value: name });
    return hook;
}

/**
 * Per-entity factory: given an entity and action, returns a hook factory
 * that the consumer parameterises with endpoint/body shape. The result is
 * itself a hook (call it like any other hook).
 */
function entityFactory<E extends EntityName, A extends EntityActions[E]>(entity: E, action: A) {
    return <TParams, TData = unknown>(override: MutationOverride<TParams, TData>) =>
        buildHook<TParams, TData>({
            entity,
            action: action as string,
            name: `use${capitalize(action as string)}${capitalize(entity)}`,
            ...override,
        });
}

function capitalize<S extends string>(s: S): Capitalize<S> {
    return (s.charAt(0).toUpperCase() + s.slice(1)) as Capitalize<S>;
}

// --- Create / Update / Delete proxies --------------------------------------

export const useCreate = {
    scene: entityFactory('scene', 'create'),
    act: entityFactory('act', 'create'),
    fragment: entityFactory('fragment', 'create'),
    character: entityFactory('character', 'create'),
    relationship: entityFactory('relationship', 'create'),
    relationType: entityFactory('relationType', 'create'),
    episode: entityFactory('episode', 'create'),
    feature: entityFactory('feature', 'create'),
    tag: entityFactory('tag', 'create'),
    bit: entityFactory('bit', 'create'),
    textAtom: entityFactory('textAtom', 'create'),
    sceneNote: entityFactory('sceneNote', 'create'),
    actNote: entityFactory('actNote', 'create'),
    fragmentNote: entityFactory('fragmentNote', 'create'),
    actTextRow: entityFactory('act', 'addTextRow'),
    fragmentTextRow: entityFactory('fragment', 'addTextRow'),
    sceneTextRow: entityFactory('scene', 'addTextRow'),
    project: entityFactory('project', 'create'),
    comment: entityFactory('comment', 'create'),
    childComment: entityFactory('comment', 'createChild'),
    snapshot: entityFactory('snapshot', 'create'),
    characterLine: entityFactory('characterLine', 'create'),
    asset: entityFactory('asset', 'create'),
    line: entityFactory('line', 'create'),
} as const;

export const useUpdate = {
    scene: entityFactory('scene', 'update'),
    sceneMeta: entityFactory('scene', 'updateMeta'),
    sceneTags: entityFactory('scene', 'updateTags'),
    sceneMove: entityFactory('scene', 'move'),
    sceneDraft: entityFactory('scene', 'setDraft'),
    sceneLogline: entityFactory('scene', 'updateLogline'),
    sceneSynopsis: entityFactory('scene', 'updateSynopsis'),
    sceneBulkMove: entityFactory('scene', 'bulkMove'),
    act: entityFactory('act', 'update'),
    actLogline: entityFactory('act', 'updateLogline'),
    actSynopsis: entityFactory('act', 'updateSynopsis'),
    fragment: entityFactory('fragment', 'update'),
    fragmentLogline: entityFactory('fragment', 'updateLogline'),
    fragmentSynopsis: entityFactory('fragment', 'updateSynopsis'),
    character: entityFactory('character', 'update'),
    relationship: entityFactory('relationship', 'update'),
    episode: entityFactory('episode', 'update'),
    feature: entityFactory('feature', 'update'),
    tag: entityFactory('tag', 'update'),
    tagColor: entityFactory('tag', 'updateColor'),
    bit: entityFactory('bit', 'update'),
    textAtom: entityFactory('textAtom', 'update'),
    sceneNoteBody: entityFactory('sceneNote', 'updateBody'),
    actNoteBody: entityFactory('actNote', 'updateBody'),
    fragmentNoteBody: entityFactory('fragmentNote', 'updateBody'),
    project: entityFactory('project', 'update'),
    projectArchive: entityFactory('project', 'archive'),
    projectRestore: entityFactory('project', 'restore'),
    comment: entityFactory('comment', 'update'),
    notifications: entityFactory('notification', 'update'),
    characterLine: entityFactory('characterLine', 'update'),
    asset: entityFactory('asset', 'update'),
    line: entityFactory('line', 'update'),
} as const;

export const useDelete = {
    scene: entityFactory('scene', 'delete'),
    act: entityFactory('act', 'delete'),
    fragment: entityFactory('fragment', 'delete'),
    character: entityFactory('character', 'delete'),
    relationship: entityFactory('relationship', 'delete'),
    relationType: entityFactory('relationType', 'delete'),
    episode: entityFactory('episode', 'delete'),
    feature: entityFactory('feature', 'delete'),
    tag: entityFactory('tag', 'delete'),
    bit: entityFactory('bit', 'delete'),
    textAtom: entityFactory('textAtom', 'delete'),
    sceneNote: entityFactory('sceneNote', 'delete'),
    actNote: entityFactory('actNote', 'delete'),
    fragmentNote: entityFactory('fragmentNote', 'delete'),
    actTextRow: entityFactory('act', 'removeTextRow'),
    fragmentTextRow: entityFactory('fragment', 'removeTextRow'),
    sceneTextRow: entityFactory('scene', 'removeTextRow'),
    project: entityFactory('project', 'delete'),
    comment: entityFactory('comment', 'delete'),
    notification: entityFactory('notification', 'delete'),
    characterLine: entityFactory('characterLine', 'delete'),
    asset: entityFactory('asset', 'delete'),
    line: entityFactory('line', 'delete'),
} as const;
